import {BaseService} from './base';
import {appDebug} from '../util';
import emitter from '../util/emitter';


const moduleDebug = appDebug.extend('uploadService');

const emptyGif = Object.freeze({
    'tags': [],
    'long_title': '',
    'bg_color': '#3f3f3f',
    'unprocessed': true,
    'media': [
        {
            'nanogif': {'dims': [200, 200], 'url': ''},
            'tinygif': {'dims': [200, 200], 'url': ''},
            'mediumgif': {'dims': [200, 200], 'url': ''},
            'gif': {'dims': [200, 200], 'url': ''},
        },
    ],
});

export class UploadService extends BaseService {
    unprocessedUploads: object;
    processedUploads: object;
    fetchingInProgress: boolean;

    constructor(context: jsx.AppGlobals) {
        super(context, {});
        this.unprocessedUploads = {};
        this.processedUploads = {};
        this.fetchingInProgress = false;

        const unprocessedUploadsStr = context.storageService.getItem(
            'unprocessedUploads');
        if (unprocessedUploadsStr) {
            this.unprocessedUploads = JSON.parse(unprocessedUploadsStr);
            const oneDayAgo = Date.now() - (1000 * 60 * 60 * 24);
            for (const [id, gif] of Object.entries(this.unprocessedUploads)) {
                if (!gif.unprocessedTimestamp ||
                    gif.unprocessedTimestamp < oneDayAgo) {
                    moduleDebug('removing unprocessedUpload', id);
                    delete this.unprocessedUploads[id];
                }
            }
            this.storeUnprocessedUpload();
        }
    }
    unprocessedUploadsExist() {
        return Object.keys(this.unprocessedUploads).length > 0;
    }
    fetchUnprocessed() {
        const functionDebug = moduleDebug.extend('fetchUnprocessed');
        const promises = [];
        this.fetchingInProgress = true;
        if (this.unprocessedUploadsExist()) {
            const v2Ids = Object.keys(this.unprocessedUploads);
            const handle = ({body}) => {
                if (body.results) {
                    body.results.forEach((gifData) => {
                        if (gifData.media_formats) {
                            const gif = this.unprocessedUploads[gifData.id];
                            if (gif) {
                                gif.unprocessed = false;
                                for (const key of Object.keys(gifData)) {
                                    gif[key] = gifData[key];
                                }
                                this.processUpload(gif.id);
                                emitter.emit('uploads-processed');
                            }
                        }
                    });
                }
            };
            functionDebug('ids', {v2Ids});
            v2Ids.length && promises.push(
                this.context.apiService.getUploadedGifsV2(v2Ids).then(handle));
        } else {
            functionDebug('no ids');
        }
        Promise.all(promises).then(() => {
            this.fetchingInProgress = false;
        });
    }
    processUpload(id) {
        moduleDebug('processUpload', id);
        const processedGif = structuredClone(this.unprocessedUploads[id]);
        delete this.unprocessedUploads[id];
        this.processedUploads[id] = processedGif;
        this.storeUnprocessedUpload();
    }
    removeUnprocessedUpload(id) {
        moduleDebug('removeUnprocessedUpload', id);
        delete this.unprocessedUploads[id];
        this.storeUnprocessedUpload();
    }
    removeProcessedUpload(id) {
        moduleDebug('removeProcessedUpload', id);
        delete this.processedUploads[id];
    }
    clearProcessed() {
        moduleDebug('clearProcessed');
        this.processedUploads = {};
    }
    clearAll() {
        moduleDebug('clearAll');
        this.processedUploads = {};
        this.unprocessedUploads = {};
        this.storeUnprocessedUpload();
    }
    addUnprocessedUpload(id, gif) {
        moduleDebug('addUnprocessedUpload', id, gif);
        let newGif = {...structuredClone(emptyGif), ...structuredClone(gif)};
        newGif = this.cleanGif(newGif);
        this.unprocessedUploads[id] = newGif;
        this.unprocessedUploads[id].unprocessedTimestamp = Date.now();
        try {
            this.storeUnprocessedUpload();
        } catch (err) {
            moduleDebug('File size exceeds local storage limit', err);
            this.unprocessedUploads[id] = structuredClone(emptyGif);
            this.unprocessedUploads[id].unprocessedTimestamp = Date.now();
            this.storeUnprocessedUpload();
        }
    }
    storeUnprocessedUpload() {
        this.context.storageService.setItem(
            'unprocessedUploads',
            JSON.stringify(this.unprocessedUploads),
        );
    }
    cleanGif(gif) {
        // delete unused media to reduce file size
        if (gif.media_formats) {
            delete gif.media_formats.nanogif;
            // delete gif.media_formats.tinygif;
            delete gif.media_formats.mediumgif;
            delete gif.media_formats.gif;
        }
        gif.unprocessed = true;
        return gif;
    }
}
